<template>
    <div class="conversation-analytics">
        <a-spin :spinning="loading">
            <div v-if="!loading && data && data.conversation_analytics">
                <div class="toolbar">
                    <div class="buttons">
                        <IconButton
                            :classNames="viewMode === 'chart' ? 'selected' : ''"
                            :onClick="() => (viewMode = 'chart')"
                            v-tippy="{ content: 'Grafik Görünümü' }"
                        >
                            <md-icon>insights</md-icon>
                        </IconButton>
                        <IconButton
                            :classNames="viewMode === 'pie' ? 'selected' : ''"
                            :onClick="() => (viewMode = 'pie')"
                            v-tippy="{ content: 'Pasta Grafik' }"
                        >
                            <md-icon>donut_small</md-icon>
                        </IconButton>
                        <IconButton
                            :classNames="viewMode === 'table' ? 'selected' : ''"
                            :onClick="() => (viewMode = 'table')"
                            v-tippy="{ content: 'Tablo Görünümü' }"
                        >
                            <md-icon>table_view</md-icon>
                        </IconButton>
                        <IconButton :onClick="exportToExcel" v-tippy="{ content: 'Excel İndir' }">
                            <md-icon>download</md-icon>
                        </IconButton>
                    </div>
                </div>

                <div class="stats-row">
                    <StatCard title="Toplam Konuşma" :value="totalConversations" unit="konuşma" />
                    <StatCard title="Toplam Maliyet" :value="totalCost.toFixed(2)" unit="$" />
                    <StatCard title="Ortalama Maliyet" :value="avgCostPerConversation.toFixed(4)" unit="$/konuşma" />
                </div>

                <div class="category-breakdown">
                    <h3>Kategori Bazlı Dağılım</h3>
                    <div class="category-cards">
                        <div v-for="(value, category) in categoryBreakdown" :key="category" class="category-card">
                            <div class="category-name">{{ getCategoryLabel(category) }}</div>
                            <div class="category-value">{{ value.conversations }} konuşma</div>
                            <div class="category-cost">${{ value.cost.toFixed(2) }}</div>
                        </div>
                    </div>
                </div>

                <div class="chart-container" v-if="viewMode === 'chart'">
                    <StackedAreaChart
                        title="Konuşma İstatistikleri"
                        :x-axis-data="getXAxisData()"
                        :legends="getCategoryLegends()"
                        :colors="categoryColors"
                        :series="getCategorySeries()"
                    />
                </div>

                <div class="pie-container" v-if="viewMode === 'pie'">
                    <PieChartV2 title="Kategori Dağılımı" :legends="getCategoryLegends()" :series="getPieSeries()" />
                </div>

                <div class="table-container" v-if="viewMode === 'table'">
                    <a-table :columns="columns" :data-source="tableData" :pagination="{ pageSize: 10 }" size="middle" />
                </div>
            </div>

            <div v-else-if="!loading" class="empty-state">
                <a-empty description="Veri bulunamadı. Lütfen tarih aralığını kontrol edin." />
            </div>
        </a-spin>
    </div>
</template>

<script>
import Vue from "vue";
import moment from "moment";
import _ from "lodash";

import { expertToExcel } from "../../../../../utils/utils";
import StackedAreaChart from "../../../Chatbot/pages/reports/charts/StackedAreaChart.vue";
import PieChartV2 from "../../../Chatbot/pages/reports/charts/PieChartV2.vue";
import StatCard from "../../../Chatbot/pages/reports/charts/StatCard.vue";
import IconButton from "../../../Settings/components/IconButton.vue";

export default Vue.extend({
    name: "ConversationAnalytics",
    components: {
        StackedAreaChart,
        PieChartV2,
        StatCard,
        IconButton,
    },
    props: {
        channel: {
            type: Object,
            required: true,
        },
        filters: {
            type: Object,
            required: true,
        },
        loading: {
            type: Boolean,
            default: false,
        },
        data: {
            type: Object,
            default: null,
        },
    },
    data() {
        return {
            viewMode: "chart",
            categoryColors: ["#80FFA5", "#00DDFF", "#37A2FF", "#FF0087", "#FFBF00"],
            categoryLabels: {
                AUTHENTICATION: "Kimlik Doğrulama",
                MARKETING: "Pazarlama",
                SERVICE: "Hizmet",
                UTILITY: "Yardımcı",
            },
            columns: [
                {
                    title: "Tarih",
                    dataIndex: "date",
                    key: "date",
                },
                {
                    title: "Kategori",
                    dataIndex: "category",
                    key: "category",
                    filters: [
                        { text: "Kimlik Doğrulama", value: "AUTHENTICATION" },
                        { text: "Pazarlama", value: "MARKETING" },
                        { text: "Hizmet", value: "SERVICE" },
                        { text: "Yardımcı", value: "UTILITY" },
                    ],
                    onFilter: (value, record) => record.categoryRaw === value,
                },
                {
                    title: "Tip",
                    dataIndex: "type",
                    key: "type",
                },
                {
                    title: "Konuşma Sayısı",
                    dataIndex: "conversations",
                    key: "conversations",
                    sorter: (a, b) => a.conversations - b.conversations,
                },
                {
                    title: "Maliyet",
                    dataIndex: "cost",
                    key: "cost",
                    sorter: (a, b) => parseFloat(a.costRaw) - parseFloat(b.costRaw),
                },
            ],
        };
    },
    computed: {
        dataPoints() {
            const data = _.get(this.data, "conversation_analytics.data", []) || [];
            if (data.length > 0) {
                return _.get(data, "0.data_points", []) || [];
            }
            return [];
        },
        totalConversations() {
            return this.dataPoints.reduce((sum, dp) => sum + (dp.conversation || 0), 0);
        },
        totalCost() {
            return this.dataPoints.reduce((sum, dp) => sum + (dp.cost || 0), 0);
        },
        avgCostPerConversation() {
            if (this.totalConversations === 0) return 0;
            return this.totalCost / this.totalConversations;
        },
        categoryBreakdown() {
            const breakdown = {};
            this.dataPoints.forEach((dp) => {
                const category = dp.conversation_category || "OTHER";
                if (!breakdown[category]) {
                    breakdown[category] = { conversations: 0, cost: 0 };
                }
                breakdown[category].conversations += dp.conversation || 0;
                breakdown[category].cost += dp.cost || 0;
            });
            return breakdown;
        },
        tableData() {
            return this.dataPoints.map((dp, index) => ({
                key: index,
                date: moment.unix(dp.start).format("DD/MM/YYYY"),
                category: this.getCategoryLabel(dp.conversation_category),
                categoryRaw: dp.conversation_category,
                type: dp.conversation_type || "-",
                conversations: dp.conversation || 0,
                cost: "$" + (dp.cost || 0).toFixed(2),
                costRaw: dp.cost || 0,
            }));
        },
    },
    methods: {
        getCategoryLabel(category) {
            return this.categoryLabels[category] || category || "Diğer";
        },
        getCategoryLegends() {
            return Object.keys(this.categoryBreakdown).map((cat) => this.getCategoryLabel(cat));
        },
        getXAxisData() {
            const dates = _.uniq(this.dataPoints.map((dp) => moment.unix(dp.start).format("DD/MM")));
            return dates;
        },
        getCategorySeries() {
            const categories = Object.keys(this.categoryBreakdown);
            const dates = _.uniq(this.dataPoints.map((dp) => moment.unix(dp.start).format("DD/MM")));

            return categories.map((category, idx) => {
                const data = dates.map((date) => {
                    const point = this.dataPoints.find(
                        (dp) => moment.unix(dp.start).format("DD/MM") === date && dp.conversation_category === category
                    );
                    return point ? point.conversation : 0;
                });

                return {
                    name: this.getCategoryLabel(category),
                    type: "line",
                    stack: "Total",
                    smooth: true,
                    lineStyle: { width: 0 },
                    showSymbol: false,
                    areaStyle: { opacity: 0.8 },
                    emphasis: { focus: "series" },
                    data: data,
                };
            });
        },
        getPieSeries() {
            return [
                {
                    name: "Konuşma Dağılımı",
                    type: "pie",
                    radius: ["40%", "70%"],
                    avoidLabelOverlap: false,
                    label: {
                        show: true,
                        formatter: "{b}: {c} ({d}%)",
                    },
                    data: Object.entries(this.categoryBreakdown).map(([category, value]) => ({
                        name: this.getCategoryLabel(category),
                        value: value.conversations,
                    })),
                },
            ];
        },
        exportToExcel() {
            const exportData = this.tableData.map((row) => ({
                Tarih: row.date,
                Kategori: row.category,
                Tip: row.type,
                "Konuşma Sayısı": row.conversations,
                Maliyet: row.cost,
            }));
            expertToExcel(exportData, "conversation_analytics.xlsx", "Conversation Analytics");
        },
    },
});
</script>

<style scoped lang="scss">
.conversation-analytics {
    padding: 20px;

    .toolbar {
        display: flex;
        justify-content: flex-end;
        margin-bottom: 20px;

        .buttons {
            display: flex;
            border: 1px solid var(--color-border-default);
            border-radius: 4px;

            .icon-button {
                padding: 8px;
                display: flex;
                justify-content: center;
                align-items: center;
                border-right: 1px solid var(--color-border-default);
                border-radius: unset !important;

                &.selected {
                    background-color: var(--color-bg-info-low-hover);
                }

                &:last-child {
                    border-right: none;
                }

                &:hover {
                    background-color: var(--color-bg-neutral-pressed);
                }
            }
        }
    }

    .stats-row {
        display: flex;
        gap: 20px;
        margin-bottom: 30px;
        flex-wrap: wrap;

        .stat-card {
            flex: 1;
            min-width: 200px;
        }
    }

    .category-breakdown {
        margin-bottom: 30px;

        h3 {
            font-size: 16px;
            font-weight: 600;
            margin-bottom: 15px;
        }

        .category-cards {
            display: flex;
            gap: 15px;
            flex-wrap: wrap;

            .category-card {
                padding: 15px 20px;
                background: var(--color-bg-subtle);
                border-radius: 8px;
                min-width: 150px;

                .category-name {
                    font-weight: 600;
                    margin-bottom: 8px;
                    color: var(--color-text-strong-default);
                }

                .category-value {
                    font-size: 18px;
                    font-weight: 700;
                    color: var(--color-text-info);
                }

                .category-cost {
                    font-size: 14px;
                    color: var(--color-text-default);
                    margin-top: 4px;
                }
            }
        }
    }

    .chart-container,
    .pie-container {
        min-height: 400px;
        margin-bottom: 30px;
    }

    .table-container {
        margin-bottom: 30px;
    }

    .empty-state {
        padding: 60px 20px;
        text-align: center;
    }
}
</style>
